import Select from 'antd/es/select';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { hasValue } from 'utils/hasValue';

import { useGetCustomerCreditors } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';

import { useDraftDefinition } from '../contexts/DraftDefinitionProvider';
import { useDraftProgress } from '../contexts/DraftProgressProvider';
import { useDraftStepSubmitBus } from '../contexts/DraftStepSubmitBus';
import { useDraftStepFields } from '../hooks/useDraftStepFields';
import { loadReceiverInformation } from '../utils/loadReceiverInformation';
import type { StepFormExtensionProps } from '../utils/types';

export const SelectReceiver = ({ step }: StepFormExtensionProps) => {
  const { getValues, setValue } = useFormContext();
  const { onSuccessfulSubmit } = useDraftStepSubmitBus();
  const { setData, selectedReceiver, setSelectedReceiver } = useDraftProgress();
  const { product } = useDraftDefinition();

  const organisationId = useOrganisationId<number>();

  const { data: savedReceivers = [] } = useGetCustomerCreditors(
    organisationId,
    {
      product_id: Number(product.product_id),
      schema_id: Number(product.schema_id)
    },
    {
      query: {
        enabled: hasValue(product.product_id) && hasValue(product.schema_id),
        select(data) {
          return data.data;
        }
      }
    }
  );

  const { isReceiverManagementEnabled } = useFeatureAccessControl();

  const { fields: receiverFields } = useDraftStepFields('creditor');
  const { fields: receiverBankFields } = useDraftStepFields('creditor_account');
  const { fields: intermediaryAgentFields } =
    useDraftStepFields('intermediary_agent');
  const { fields: intermediaryAgentAccountFields } = useDraftStepFields(
    'intermediary_agent_account'
  );

  const onSelectedReceiver = useCallback(
    (receiverId: number) => {
      const receiver = savedReceivers.find(
        (savedReceiver) => savedReceiver.id === receiverId
      );

      setSelectedReceiver(receiver);

      if (receiver === undefined) {
        receiverFields.forEach((field) => {
          setValue(field.fullName, undefined);
        });
        return;
      }

      Object.entries(receiver).forEach(([key, value]) => {
        // This is a special case, because in the db we have full_name, in the FE it's called name
        if (key === 'full_name') {
          setValue('creditor.name', receiver.full_name);
        }
        if (key === 'creditor_type') {
          setValue('creditor.type', receiver.creditor_type);
        }

        const hasField = receiverFields.find((field) => field.name === key);

        if (hasField) {
          setValue(`creditor.${key}`, value);
        }
      });
    },
    [receiverFields, savedReceivers, setSelectedReceiver, setValue]
  );

  useEffect(() => {
    return onSuccessfulSubmit(() => {
      if (selectedReceiver) {
        const receiverBankValues = loadReceiverInformation(
          selectedReceiver.creditor_details,
          receiverBankFields,
          'creditor_account'
        );

        const intermediaryAgentValues = loadReceiverInformation(
          selectedReceiver.creditor_details,
          intermediaryAgentFields,
          'intermediary_agent'
        );

        const intermediaryAgentAccountValues = loadReceiverInformation(
          selectedReceiver.creditor_details,
          intermediaryAgentAccountFields,
          'intermediary_agent_account'
        );

        setData((prev) => ({
          ...prev,
          ...receiverBankValues,
          ...intermediaryAgentValues,
          ...intermediaryAgentAccountValues
        }));
      }
    });
  }, [
    getValues,
    intermediaryAgentAccountFields,
    intermediaryAgentFields,
    onSuccessfulSubmit,
    receiverBankFields,
    selectedReceiver,
    setData,
    step
  ]);

  if (isReceiverManagementEnabled === false) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-1">
      <span className="w-fit text-sm">Recent</span>
      <Select
        value={selectedReceiver?.id}
        onChange={(receiverId) => onSelectedReceiver(receiverId)}
        className="h-10"
        allowClear
      >
        {savedReceivers?.map((receiver) => (
          <Select.Option key={receiver.id} value={receiver.id}>
            {receiver.full_name}
          </Select.Option>
        ))}
      </Select>
      <hr className="mt-2" />
    </div>
  );
};
